import { format } from "date-fns";
import React, {
    useEffect,
    useMemo,
    useState,
} from "react";

import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";

import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

import { DateRange } from "react-date-range";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";

import {
    BUTTONS,
} from "@/utils/constants/appConstants/commonConstants";

import { COLORS } from "@/utils/constants/colors";
import { cn } from "@/lib/utils";

interface Props {
    handleSelectChange?: (
        selectedStatusOption: any
    ) => void;

    options?: any;

    selectedStatusOption?: any;

    setSelectedStatusOption?: (
        selectedStatusOption: any
    ) => void;

    colorStyles: any;

    handleReset?: () => void;

    handleApplyFilter?: (
        filterOverride?: any
    ) => void;

    filter: any;

    setFilter: (
        filter: any
    ) => void;

    groups?: any;

    roles?: any;

    title?: string;

    setPageNo?: (
        pageNo: number
    ) => void;

    id: string;
}

const ALL_FILTER_VALUE = "**all**";

type FilterSelectOption = {
    value: string;
    label: string;
};

const toSelectItemValue = (
    value: unknown
): string | null => {
    if (
        value === "" ||
        value === null ||
        value === undefined
    ) {
        return null;
    }

    return String(value);
};

interface FilterSelectFieldProps {
    id: string;
    placeholder: string;
    value: string;
    options: FilterSelectOption[];
    onValueChange: (
        value: string
    ) => void;
    onOpenChange?: (
        open: boolean
    ) => void;
    className?: string;
}

const FilterSelectField = ({
    id,
    placeholder,
    value,
    options,
    onValueChange,
    onOpenChange,
    className,
}: FilterSelectFieldProps) => {
    const selectedLabel =
        options.find(
            (option) =>
                option.value === value
        )?.label;

    const showPlaceholder =
        !selectedLabel ||
        value === ALL_FILTER_VALUE;

    return (
        <DropdownMenu
            modal={false}
            onOpenChange={onOpenChange}
        >
            <DropdownMenuTrigger
                asChild
            >
                <button
                    type="button"
                    id={id}
                    className={cn(
                        "gs-filter-select-trigger mt-2",
                        className
                    )}
                >
                    <span
                        className={
                            showPlaceholder
                                ? "gs-filter-select-trigger__placeholder"
                                : undefined
                        }
                    >
                        {showPlaceholder
                            ? placeholder
                            : selectedLabel}
                    </span>

                    <em
                        className="icon ni ni-chevron-down gs-filter-select-trigger__chevron"
                        aria-hidden
                    />
                </button>
            </DropdownMenuTrigger>

            <DropdownMenuContent
                align="start"
                sideOffset={1}
                className="gs-filter-select-content"
            >
                {options.map(
                    (option) => (
                        <DropdownMenuItem
                            key={
                                option.value
                            }
                            className="gs-filter-select-item"
                            onClick={() =>
                                onValueChange(
                                    option.value
                                )
                            }
                        >
                            {
                                option.label
                            }
                        </DropdownMenuItem>
                    )
                )}
            </DropdownMenuContent>
        </DropdownMenu>
    );
};

const FilterOverlay = (
    props: Props
) => {
    const [open, setOpen] =
        useState(false);

    /**
     * IMPORTANT:
     *
     * draftFilter contains the filters
     * selected inside the popup.
     *
     * It does NOT become the actual
     * applied filter until Apply is clicked.
     */
    const [draftFilter, setDraftFilter] =
        useState<any>(
            props.filter ?? {}
        );

    const [
        selectedStatus,
        setSelectedStatus,
    ] = useState<string>(
        ALL_FILTER_VALUE
    );

    const [
        selectedGroup,
        setSelectedGroup,
    ] = useState<string>(
        ALL_FILTER_VALUE
    );

    const [
        selectedRole,
        setSelectedRole,
    ] = useState<string>(
        ALL_FILTER_VALUE
    );

    const [
        ownerSearch,
        setOwnerSearch,
    ] = useState(
        props.filter?.owner || ""
    );

    const [
        openCreatedAtDate,
        setCreatedAtDateOpen,
    ] = useState(false);

    const [
        openLastLoginAtDate,
        setLastLoginAtDateOpen,
    ] = useState(false);

    const [
        createdAtSelectionRange,
        setCreatedAtSelectionRange,
    ] = useState<any>({
        startDate: undefined,
        endDate: undefined,
        key: "selection",
    });

    const [
        lastLoginAtSelectionRange,
        setLastLoginAtSelectionRange,
    ] = useState<any>({
        startDate: undefined,
        endDate: undefined,
        key: "selection",
    });

    const [
        createdAtSelectingTill,
        setCreatedAtSelectingTill,
    ] = useState(false);

    const [
        lastLoginAtSelectingTill,
        setLastLoginAtSelectingTill,
    ] = useState(false);

    const parseFilterDate = (
        value?: string
    ): Date | undefined => {
        if (!value) {
            return undefined;
        }
    
        const [year, month, day] =
            value.split("-").map(Number);
    
        return new Date(
            year,
            month - 1,
            day
        );
    };

    const syncDraftFromFilter = (currentFilter: Record<string, any> = {}) => {
        setDraftFilter(currentFilter);
        setOwnerSearch(currentFilter.owner || "");
        setSelectedStatus(
            currentFilter.status?.value !== null &&
            currentFilter.status?.value !== undefined
                ? String(currentFilter.status.value)
                : ALL_FILTER_VALUE
        );
        setSelectedGroup(
            currentFilter.group?.value
                ? String(currentFilter.group.value)
                : ALL_FILTER_VALUE
        );
        setSelectedRole(
            currentFilter.role?.value
                ? String(currentFilter.role.value)
                : ALL_FILTER_VALUE
        );
        setCreatedAtSelectionRange({
            startDate: parseFilterDate(currentFilter.createdAtStart),
            endDate: parseFilterDate(currentFilter.createdAtEnd),
            key: "selection",
        });
        setLastLoginAtSelectionRange({
            startDate: parseFilterDate(currentFilter.lastLoginAtStart),
            endDate: parseFilterDate(currentFilter.lastLoginAtEnd),
            key: "selection",
        });
        setCreatedAtSelectingTill(false);
        setLastLoginAtSelectingTill(false);
    };

    /**
     * Sync local draft state when the
     * actual applied filter changes.
     */
    useEffect(() => {
        syncDraftFromFilter(props.filter ?? {});
    }, [props.filter]);

    /*
     * STATUS
     */
    const handleStatusChange = (
        value: string
    ) => {

        const option =
            value ===
            ALL_FILTER_VALUE
                ? null
                : props.options?.find(
                      (item: any) =>
                          String(
                              item.value
                          ) === value
                  ) ?? null;
        setSelectedStatus(value);

        setDraftFilter(
            (prev: any) => ({
                ...prev,
                status: option,
            })
        );
    };

    /*
     * GROUP
     */
    const handleGroupChange = (
        value: string
    ) => {
        const option =
            value ===
            ALL_FILTER_VALUE
                ? null
                : props.groups?.find(
                      (group: any) =>
                          String(
                              group.id
                          ) === value
                  ) ?? null;

        setSelectedGroup(value);

        setDraftFilter(
            (prev: any) => ({
                ...prev,
                group: option
                    ? {
                          label: option.name,
                          value: option.id,
                      }
                    : null,
            })
        );
    };

    /*
     * ROLE
     */
    const handleRoleChange = (
        value: string
    ) => {
        const option =
            value ===
            ALL_FILTER_VALUE
                ? null
                : props.roles?.find(
                      (role: any) =>
                          String(
                              role.id
                          ) === value
                  ) ?? null;

        setSelectedRole(value);

        setDraftFilter(
            (prev: any) => ({
                ...prev,
                role: option
                    ? {
                          label: option.name,
                          value: option.id,
                      }
                    : null,
            })
        );
    };

    /*
     * CREATED AT
     */
    const handleCreatedAtClick =
        () => {
            setLastLoginAtDateOpen(
                false
            );

            setCreatedAtDateOpen(
                (prev) => !prev
            );
        };

    const handleCreatedAtSelectRange =
        (ranges: any) => {
            const {
                startDate,
                endDate,
            } = ranges.selection;

            if (
                !createdAtSelectingTill
            ) {
                setCreatedAtSelectingTill(
                    true
                );

                setCreatedAtSelectionRange(
                    {
                        startDate,
                        endDate:
                            undefined,
                        key: "selection",
                    }
                );

                setDraftFilter(
                    (prev: any) => ({
                        ...prev,
                        createdAtStart:
                            startDate
                                ? format(
                                      new Date(
                                          startDate
                                      ),
                                      "yyyy-MM-dd"
                                  )
                                : "",
                        createdAtEnd: "",
                    })
                );

                return;
            }

            setCreatedAtSelectionRange(
                {
                    startDate,
                    endDate,
                    key: "selection",
                }
            );

            setCreatedAtSelectingTill(
                false
            );

            setCreatedAtDateOpen(
                false
            );

            setDraftFilter(
                (prev: any) => ({
                    ...prev,
                    createdAtStart:
                        startDate
                            ? format(
                                  new Date(
                                      startDate
                                  ),
                                  "yyyy-MM-dd"
                              )
                            : "",
                    createdAtEnd:
                        endDate
                            ? format(
                                  new Date(
                                      endDate
                                  ),
                                  "yyyy-MM-dd"
                              )
                            : "",
                })
            );
        };

    /*
     * LAST LOGIN AT
     */
    const handleLastLoginAtClick =
        () => {
            setCreatedAtDateOpen(
                false
            );

            setLastLoginAtDateOpen(
                (prev) => !prev
            );
        };

    const handleLastLoginAtSelectRange =
        (ranges: any) => {
            const {
                startDate,
                endDate,
            } = ranges.selection;

            if (
                !lastLoginAtSelectingTill
            ) {
                setLastLoginAtSelectingTill(
                    true
                );

                setLastLoginAtSelectionRange(
                    {
                        startDate,
                        endDate:
                            undefined,
                        key: "selection",
                    }
                );

                setDraftFilter(
                    (prev: any) => ({
                        ...prev,
                        lastLoginAtStart:
                            startDate
                                ? format(
                                      new Date(
                                          startDate
                                      ),
                                      "yyyy-MM-dd"
                                  )
                                : "",
                        lastLoginAtEnd: "",
                    })
                );

                return;
            }

            setLastLoginAtSelectionRange(
                {
                    startDate,
                    endDate,
                    key: "selection",
                }
            );

            setLastLoginAtSelectingTill(
                false
            );

            setLastLoginAtDateOpen(
                false
            );

            setDraftFilter(
                (prev: any) => ({
                    ...prev,
                    lastLoginAtStart:
                        startDate
                            ? format(
                                  new Date(
                                      startDate
                                  ),
                                  "yyyy-MM-dd"
                              )
                            : "",
                    lastLoginAtEnd:
                        endDate
                            ? format(
                                  new Date(
                                      endDate
                                  ),
                                  "yyyy-MM-dd"
                              )
                            : "",
                })
            );
        };

    /*
     * CLEAR CREATED AT
     */
    const handleClearCreatedAtDate =
        () => {
            setCreatedAtSelectingTill(
                false
            );

            setCreatedAtSelectionRange(
                {
                    startDate:
                        undefined,
                    endDate:
                        undefined,
                    key: "selection",
                }
            );

            setDraftFilter(
                (prev: any) => ({
                    ...prev,
                    createdAtStart: "",
                    createdAtEnd: "",
                })
            );

            setCreatedAtDateOpen(
                false
            );
        };

    /*
     * CLEAR LAST LOGIN
     */
    const handleClearLastLoginAtDate =
        () => {
            setLastLoginAtSelectingTill(
                false
            );

            setLastLoginAtSelectionRange(
                {
                    startDate:
                        undefined,
                    endDate:
                        undefined,
                    key: "selection",
                }
            );

            setDraftFilter(
                (prev: any) => ({
                    ...prev,
                    lastLoginAtStart: "",
                    lastLoginAtEnd: "",
                })
            );

            setLastLoginAtDateOpen(
                false
            );
        };

    /*
     * APPLY
     *
     * API is triggered from TemplateList
     * only after this function calls
     * props.handleApplyFilter().
     */
    const handleApplyFilter = () => {
        const updatedFilter = {
            ...draftFilter,
            owner: ownerSearch.trim(),
        };
    
        const hasActiveFilter =
            selectedStatus !== ALL_FILTER_VALUE ||
            selectedGroup !== ALL_FILTER_VALUE ||
            selectedRole !== ALL_FILTER_VALUE ||
            ownerSearch.trim() !== "" ||
            !!createdAtSelectionRange.startDate ||
            !!createdAtSelectionRange.endDate ||
            !!lastLoginAtSelectionRange.startDate ||
            !!lastLoginAtSelectionRange.endDate;
    
        const filterToApply = hasActiveFilter
            ? updatedFilter
            : {};
    
        setOpen(false);
    
        // Update actual/applied filter
        props.setFilter(filterToApply);
    
        // Trigger table/API update
        props.handleApplyFilter?.(filterToApply);
    };

    /*
     * RESET
     *
     * Draft-only: discard unsaved selections in the popover (no list refetch).
     * Applied filters: clear filters and let the page refetch.
     */
    const handleReset = () => {
        setCreatedAtDateOpen(false);
        setLastLoginAtDateOpen(false);

        const applied =
            props.filter && Object.keys(props.filter).length > 0;

        if (applied) {
            syncDraftFromFilter({});
            props.setFilter({});
            props.handleReset?.();
        } else {
            syncDraftFromFilter(props.filter ?? {});
        }

        setOpen(false);
    };

    const dateRangeLabel = (
        range: any
    ) => {
        if (!range.startDate) {
            return "";
        }

        const from = format(
            new Date(
                range.startDate
            ),
            "MMM dd, yyyy"
        );

        if (!range.endDate) {
            return `From: ${from}`;
        }

        const till = format(
            new Date(
                range.endDate
            ),
            "MMM dd, yyyy"
        );

        return `From: ${from} - Till: ${till}`;
    };

    const statusOptions =
        useMemo<
            FilterSelectOption[]
        >(() => {
            const items: FilterSelectOption[] =
                [
                    {
                        value: ALL_FILTER_VALUE,
                        label: "All",
                    },
                ];

            props.options?.forEach(
                (option: any) => {
                    const value =
                        toSelectItemValue(
                            option.value
                        );

                    if (
                        value !== null
                    ) {
                        items.push({
                            value,
                            label: option.label,
                        });
                    }
                }
            );

            return items;
        }, [props.options]);

    const roleOptions =
        useMemo<
            FilterSelectOption[]
        >(() => {
            return [
                {
                    value: ALL_FILTER_VALUE,
                    label: "All Roles",
                },
                ...(props.roles ??
                    []
                ).map(
                    (role: any) => ({
                        value: String(
                            role.id
                        ),
                        label: role.name,
                    })
                ),
            ];
        }, [props.roles]);

    const groupOptions =
        useMemo<
            FilterSelectOption[]
        >(() => {
            return [
                {
                    value: ALL_FILTER_VALUE,
                    label: "All Groups",
                },
                ...(props.groups ??
                    []
                ).map(
                    (group: any) => ({
                        value: String(
                            group.id
                        ),
                        label: group.name,
                    })
                ),
            ];
        }, [props.groups]);

    const closeDatePickers = () => {
        setCreatedAtDateOpen(
            false
        );

        setLastLoginAtDateOpen(
            false
        );
    };
    const hasDraftFilter = useMemo(() => {
        return (
            selectedStatus !== ALL_FILTER_VALUE ||
            selectedGroup !== ALL_FILTER_VALUE ||
            selectedRole !== ALL_FILTER_VALUE ||
            ownerSearch.trim() !== "" ||
            !!createdAtSelectionRange.startDate ||
            !!createdAtSelectionRange.endDate ||
            !!lastLoginAtSelectionRange.startDate ||
            !!lastLoginAtSelectionRange.endDate
        );
    }, [
        selectedStatus,
        selectedGroup,
        selectedRole,
        ownerSearch,
        createdAtSelectionRange,
        lastLoginAtSelectionRange,
    ]);

    const hasAppliedFilter =
        !!props.filter && Object.keys(props.filter).length > 0;

    const hasIncompleteDateRange =
        createdAtSelectingTill ||
        lastLoginAtSelectingTill ||
        (!!draftFilter.createdAtStart && !draftFilter.createdAtEnd) ||
        (!!draftFilter.lastLoginAtStart && !draftFilter.lastLoginAtEnd);

    const disabledReset = !hasDraftFilter && !hasAppliedFilter;
    const disabledApply = disabledReset || hasIncompleteDateRange;

    return (
        <Popover
        open={open}
            onOpenChange={(
                value
            ) => {
                setOpen(value);

                /**
                 * When opening the filter,
                 * start from the currently
                 * applied filter.
                 */
                if (value) {
                    setDraftFilter(
                        props.filter ??
                            {}
                    );
                }
            }}
            modal={false}
        >
            <PopoverTrigger
                asChild
            >
                <Button
                    type="button"
                    variant="light"
                    size="sm"
                    id={`${props.id}-filter-btn`}
                    className="gs-list-page-filter-btn"
                    aria-label={
                        BUTTONS.FILTER
                    }
                    title={
                        BUTTONS.FILTER
                    }
                >
                    <em
                        className="icon ni ni-filter-fill"
                        id={`${props.id}-filter-icon`}
                        aria-hidden
                    />

                    <span className="gs-list-page-filter-btn__label">
                        {
                            BUTTONS.FILTER
                        }
                    </span>
                </Button>
            </PopoverTrigger>

            <PopoverContent
                className="gs-filter-popover filter-popover-container p-0"
                align="start"
                side="bottom"
                sideOffset={8}
                collisionPadding={16}
                onOpenAutoFocus={(
                    event
                ) =>
                    event.preventDefault()
                }
            >
                <div className="gs-filter-popover__header">
                    <p
                        id={`${props.id}-filter-title`}
                        className="gs-filter-popover__title"
                    >
                        {
                            BUTTONS.FILTER
                        }
                    </p>
                </div>

                <div className="gs-filter-popover__body">
                    {props.options && (
                        <FilterSelectField
                            id={`${props.id}-filter-select-status`}
                            placeholder="Status"
                            value={
                                selectedStatus
                            }
                            options={
                                statusOptions
                            }
                            onValueChange={
                                handleStatusChange
                            }
                            onOpenChange={(
                                isOpen
                            ) => {
                                if (
                                    isOpen
                                ) {
                                    closeDatePickers();
                                }
                            }}
                        />
                    )}

                    {props.title ===
                        "Envelopes" && (
                        <Input
                            id={`${props.id}-filter-owner-input`}
                            type="text"
                            className="gs-filter-input mt-2"
                            placeholder="Owner email address"
                            value={
                                ownerSearch
                            }
                            onChange={(
                                event
                            ) => {
                                const value =
                                    event
                                        .target
                                        .value;

                                setOwnerSearch(
                                    value
                                );

                                setDraftFilter(
                                    (
                                        prev: any
                                    ) => ({
                                        ...prev,
                                        owner: value,
                                    })
                                );
                            }}
                        />
                    )}

                    {(props.title ===
                        "Admins" ||
                        props.title ===
                            "Users") && (
                        <>
                            <FilterSelectField
                                id={`${props.id}-filter-select-role`}
                                placeholder="Role"
                                value={
                                    selectedRole
                                }
                                options={
                                    roleOptions
                                }
                                onValueChange={
                                    handleRoleChange
                                }
                                onOpenChange={(
                                    isOpen
                                ) => {
                                    if (
                                        isOpen
                                    ) {
                                        closeDatePickers();
                                    }
                                }}
                            />

                            <FilterSelectField
                                id={`${props.id}-filter-select-group`}
                                placeholder="Group"
                                value={
                                    selectedGroup
                                }
                                options={
                                    groupOptions
                                }
                                onValueChange={
                                    handleGroupChange
                                }
                                onOpenChange={(
                                    isOpen
                                ) => {
                                    if (
                                        isOpen
                                    ) {
                                        closeDatePickers();
                                    }
                                }}
                            />

                            <div
                                className="filter-data mt-2 flex items-center"
                                id={`${props.id}-filter-last-login`}
                            >
                                <input
                                    id={`${props.id}-filter-last-login-input`}
                                    placeholder="Last Login At Range"
                                    value={dateRangeLabel(
                                        lastLoginAtSelectionRange
                                    )}
                                    className="date-input flex-1 border-0 bg-transparent text-sm outline-none"
                                    onClick={
                                        handleLastLoginAtClick
                                    }
                                    readOnly
                                />

                                <em
                                    id={`${props.id}-filter-last-login-input-cross-icon`}
                                    className="icon ni ni-cross font-bold cursor-pointer"
                                    onClick={
                                        handleClearLastLoginAtDate
                                    }
                                />
                            </div>

                            {openLastLoginAtDate && (
                                <DateRange
                                    ranges={[
                                        lastLoginAtSelectionRange,
                                    ]}
                                    onChange={
                                        handleLastLoginAtSelectRange
                                    }
                                    rangeColors={[
                                        COLORS.primary,
                                    ]}
                                    maxDate={
                                        new Date()
                                    }
                                    moveRangeOnFirstSelection={
                                        false
                                    }
                                    editableDateInputs={
                                        false
                                    }
                                />
                            )}
                        </>
                    )}

                    <div
                        className="filter-data mt-2 flex items-center"
                        id={`${props.id}-filter-created-at`}
                    >
                        <input
                            id={`${props.id}-filter-created-at-input`}
                            placeholder="Created At Range"
                            value={dateRangeLabel(
                                createdAtSelectionRange
                            )}
                            className="date-input flex-1 border-0 bg-transparent text-sm outline-none"
                            onClick={
                                handleCreatedAtClick
                            }
                            readOnly
                        />

                        <em
                            id={`${props.id}-filter-created-at-cross-icon`}
                            className="icon ni ni-cross font-bold cursor-pointer"
                            onClick={
                                handleClearCreatedAtDate
                            }
                        />
                    </div>

                    {openCreatedAtDate && (
                        <DateRange
                            ranges={[
                                createdAtSelectionRange,
                            ]}
                            onChange={
                                handleCreatedAtSelectRange
                            }
                            rangeColors={[
                                COLORS.primary,
                            ]}
                            maxDate={
                                new Date()
                            }
                            moveRangeOnFirstSelection={
                                false
                            }
                            editableDateInputs={
                                false
                            }
                        />
                    )}

                    <div className="filter-footer">
                        <Button
                            size="sm"
                            disabled={disabledApply}
                            onClick={
                                handleApplyFilter
                            }
                            id={`${props.id}-apply-filter-btn`}
                        >
                            {
                                BUTTONS.APPLY
                            }
                        </Button>

                        <Button
                            size="sm"
                            variant="light"
                            disabled={disabledReset}
                            className="gs-filter-reset-btn"
                            onClick={
                                handleReset
                            }
                            id={`${props.id}-reset-filter-btn`}
                        >
                            {
                                BUTTONS.RESET
                            }
                        </Button>
                    </div>
                </div>
            </PopoverContent>
        </Popover>
    );
};

export default FilterOverlay;